import React from "react";
import StepHeader from "../components/StepHeader";
import StatusBadge from "../components/StatusBadge";
import ContinueButton from "../components/ContinueButton";
import { CHECKS, CHECK_STATUS, CLASSIFICATIONS } from "../content";

/** Copy and actions for each application status after verification has started. */
const STATUS_VIEWS = {
  VERIFICATION_STARTED: {
    icon: "ri-time-line",
    tone: "warning",
    eyebrow: "In progress",
    title: "Finish your verification",
    text: "Your verification session is still open. Continue where you left off.",
    action: "continue",
    actionLabel: "Continue verification",
  },
  IN_REVIEW: {
    icon: "ri-time-line",
    tone: "warning",
    eyebrow: "Under review",
    title: "We are reviewing your details",
    text: "We will email you when your account is ready.",
    action: "explore",
    actionLabel: "Explore markets",
  },
  APPROVED: {
    icon: "ri-check-line",
    tone: "success",
    eyebrow: "Verified",
    title: "Your account is verified",
    text: "You now have full access to trading, deposits and withdrawals.",
    action: "explore",
    actionLabel: "Start trading",
  },
  REJECTED: {
    icon: "ri-close-line",
    tone: "danger",
    eyebrow: "Declined",
    title: "We could not verify your details",
    text: "Your application was declined. You can start a new application or contact support.",
    action: "restart",
    actionLabel: "Start a new application",
  },
  EXPIRED: {
    icon: "ri-hourglass-line",
    tone: "muted",
    eyebrow: "Expired",
    title: "Your verification session expired",
    text: "The session ended before it was completed. Start again to finish your verification.",
    action: "restart",
    actionLabel: "Start again",
  },
};

/**
 * I-08 / C-11 — status after Didit. No polling: the status is loaded when the modal opens,
 * and "Refresh status" makes one call while a decision is still pending.
 */
function StatusStep({
  application,
  canRefresh,
  refreshing,
  onRefresh,
  busy,
  onContinueVerification,
  onRestart,
  onExplore,
  onClose,
}) {
  const view = STATUS_VIEWS[application.status] || STATUS_VIEWS.IN_REVIEW;
  const isCorporate = application.account_type === "CORPORATE";
  const classificationLabel = application.legacy
    ? "Verified customer"
    : CLASSIFICATIONS[application.classification]?.label || "Customer";

  const actions = {
    continue: onContinueVerification,
    explore: onExplore,
    restart: onRestart,
  };

  return (
    <>
      <StepHeader title="Verification status" onClose={onClose} />

      <div className="kv-body">
        <div className="kv-status-hero">
          <div className={`kv-hero-icon kv-hero-icon--${view.tone}`} aria-hidden="true">
            <i className={view.icon} />
          </div>
          <p className={`kv-eyebrow kv-eyebrow--${view.tone}`}>{view.eyebrow}</p>
          <h2 className="kv-title">{view.title}</h2>
          <p className="kv-lead">{view.text}</p>
          {canRefresh ? (
            <button
              type="button"
              className="kv-refresh-btn"
              onClick={onRefresh}
              disabled={refreshing}
              aria-busy={refreshing}
            >
              {refreshing ? (
                <span className="kv-spinner" aria-hidden="true" />
              ) : (
                <i className="ri-refresh-line" aria-hidden="true" />
              )}
              {refreshing ? "Checking…" : "Refresh status"}
            </button>
          ) : null}
        </div>

        <section className="kv-card kv-card--highlight kv-summary">
          <span className="kv-row__icon kv-row__icon--accent" aria-hidden="true">
            <i className={isCorporate ? "ri-building-4-line" : "ri-user-3-line"} />
          </span>
          <span className="kv-row__text">
            <strong>{classificationLabel}</strong>
            <small>
              {isCorporate ? "Company classification" : "Customer classification"} · Ref {application.reference}
            </small>
          </span>
          <StatusBadge tone="success">Saved</StatusBadge>
        </section>

        {application.checks.length ? (
          <>
            <h3 className="kv-section-title">Checks</h3>
            <ul className="kv-list">
              {application.checks.map((check) => {
                const meta = CHECKS[check.key];
                const status = CHECK_STATUS[check.status] || CHECK_STATUS.PENDING;
                if (!meta) return null;
                return (
                  <li key={check.key} className="kv-card kv-row kv-row--compact">
                    <span className="kv-row__icon" aria-hidden="true">
                      <i className={meta.icon} />
                    </span>
                    <span className="kv-row__text">
                      <strong>{meta.title}</strong>
                      <small>{meta.subtitle}</small>
                    </span>
                    <StatusBadge tone={status.tone}>{status.label}</StatusBadge>
                  </li>
                );
              })}
            </ul>
          </>
        ) : null}
      </div>

      <ContinueButton onClick={actions[view.action]} busy={busy}>
        {view.actionLabel}
      </ContinueButton>
    </>
  );
}

export default StatusStep;
